// src/utils/templateRenderer.ts

import {
  LoadLetterTemplate,
  TemplateVariables,
  LoadLetterClass,
  LoadLetterAllowance
} from '../types/loadLetter';

/**
 * Template Renderer
 *
 * Converts template HTML with {{variable}} placeholders into rendered HTML
 * Generates a two-page structure:
 *   Page 1: Principal's letter with instructions
 *   Page 2: Data table with class allocations
 */

/**
 * Main render function
 * Takes a template and variables, returns fully rendered HTML
 */
export const renderTemplate = (
  template: LoadLetterTemplate,
  variables: TemplateVariables
): string => {
  // Use new template structure if available, fallback to legacy
  const useLegacyStructure = !template.letterOpeningHtml && !template.letterBodyHtml;

  if (useLegacyStructure) {
    return renderLegacyTemplate(template, variables);
  }

  // Build the full two-page template HTML
  const fullTemplate = `
    <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; line-height: 1.5; color: #333;">

      <!-- PAGE 1: LETTER -->
      <div class="page page-1" style="padding: 40px;">
        ${renderPageHeader(template, variables)}
        ${renderLetterContent(template, variables)}
        ${renderLetterFooter(template, variables)}
      </div>

      <!-- PAGE BREAK -->
      <div style="page-break-after: always; break-after: page; margin: 40px 0;"></div>

      <!-- PAGE 2: DATA TABLE -->
      <div class="page page-2" style="padding: 40px;">
        ${renderDataTable(template, variables)}
      </div>

    </div>
  `;

  // Replace all variables in the template
  return replaceVariables(fullTemplate, variables);
};

/**
 * Render page header (banner, logo, date)
 */
const renderPageHeader = (template: LoadLetterTemplate, variables: TemplateVariables): string => {
  const showLogo = template.showLogo !== false;
  const logoUrl = template.logoUrl || variables.collegeLogoUrl;

  let headerHtml = '';

  // College Banner (full width at top)
  headerHtml += `
    <div style="margin: -40px -40px 30px -40px;">
      <img src="/pageheader.jpg" alt="Salesian College Sunbury" style="width: 100%; display: block;"/>
    </div>
  `;

  // Logo (kept for backward compatibility if needed)
  if (showLogo && logoUrl && logoUrl !== '/pageheader.jpg') {
    headerHtml += `
      <div style="text-align: left; margin-bottom: 40px;">
        <img src="${logoUrl}" alt="${variables.collegeName || 'College'} Logo" style="max-height: 80px;"/>
      </div>
    `;
  }

  // Date (top right)
  headerHtml += `
    <div style="text-align: right; margin-bottom: 30px;">
      <p style="margin: 0; font-size: 14px; color: #666;">{{issueDate}}</p>
    </div>
  `;

  // Custom header HTML from template
  if (template.headerHtml) {
    headerHtml += template.headerHtml;
  }

  return headerHtml;
};

/**
 * Render letter content (opening, body, closing, signature)
 */
const renderLetterContent = (template: LoadLetterTemplate, variables: TemplateVariables): string => {
  let content = '';

  // Opening (Dear X, intro paragraph)
  if (template.letterOpeningHtml) {
    content += `
      <div class="letter-opening" style="margin-bottom: 24px; line-height: 1.6;">
        ${template.letterOpeningHtml}
      </div>
    `;
  }

  // Body (bullet points, instructions)
  if (template.letterBodyHtml) {
    // Replace meeting schedule placeholder with rendered schedule
    let bodyHtml = template.letterBodyHtml;
    if (template.meetingScheduleHtml && bodyHtml.includes('{{meetingSchedule}}')) {
      bodyHtml = bodyHtml.replace('{{meetingSchedule}}', template.meetingScheduleHtml);
    }

    // Add proper styling to body content
    bodyHtml = bodyHtml.replace('<p>Please note the following:</p>',
      '<p style="margin-bottom: 12px; font-weight: 600;">Please note the following:</p>');
    bodyHtml = bodyHtml.replace(/<ul>/g, '<ul style="margin: 12px 0; padding-left: 20px; line-height: 1.7;">');
    bodyHtml = bodyHtml.replace(/<li>/g, '<li style="margin-bottom: 12px;">');
    bodyHtml = bodyHtml.replace(/<p>/g, '<p style="margin: 12px 0; line-height: 1.6;">');

    content += `
      <div class="letter-body" style="margin-bottom: 24px;">
        ${bodyHtml}
      </div>
    `;
  }

  // Closing (thanks, closing remarks)
  if (template.letterClosingHtml) {
    content += `
      <div class="letter-closing" style="margin-bottom: 24px; margin-top: 24px; line-height: 1.6;">
        ${template.letterClosingHtml}
      </div>
    `;
  }

  // Signature
  if (template.signatureHtml) {
    let signatureHtml = template.signatureHtml;
    // Replace signature image placeholder
    if (variables.principalSignature) {
      signatureHtml = signatureHtml.replace(
        '{{principalSignature}}',
        `<img src="${variables.principalSignature}" alt="Signature" style="max-height: 60px; margin-bottom: 8px;"/>`
      );
    } else {
      signatureHtml = signatureHtml.replace('{{principalSignature}}', '');
    }
    content += `
      <div class="signature" style="margin-top: 30px;">
        ${signatureHtml}
      </div>
    `;
  }

  return content;
};

/**
 * Render letter footer (college contact info)
 */
const renderLetterFooter = (template: LoadLetterTemplate, variables: TemplateVariables): string => {
  if (!template.footerHtml) return '';

  return `
    <div class="letter-footer" style="margin-top: 50px; padding-top: 20px; border-top: 2px solid #e0e0e0; font-size: 11px; color: #666; line-height: 1.5;">
      ${template.footerHtml}
    </div>
  `;
};

/**
 * Render data table (page 2)
 */
const renderDataTable = (template: LoadLetterTemplate, variables: TemplateVariables): string => {
  return `
    <!-- College Banner (Page 2) -->
    <div style="margin: -40px -40px 30px -40px;">
      <img src="/pageheader.jpg" alt="Salesian College Sunbury" style="width: 100%; display: block;"/>
    </div>

    <!-- Teacher Header -->
    <div style="background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); color: white; padding: 20px; margin-bottom: 30px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
      <h2 style="font-size: 24px; font-weight: bold; margin: 0; letter-spacing: 0.5px;">
        TEACHER: ${variables.staffName} (${variables.staffCode})
      </h2>
    </div>

    <!-- Leadership Allowances Section -->
    ${renderAllowancesSection(variables)}

    <!-- Class Assignments Table -->
    <div style="margin-bottom: 30px;">
      <h3 style="font-size: 16px; font-weight: bold; color: #1e3a8a; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 3px solid #3b82f6;">
        CLASS ALLOCATIONS
      </h3>
      <table style="width: 100%; border-collapse: collapse; border: 2px solid #1e3a8a; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
        <thead>
          <tr style="background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); color: white;">
            <th colspan="2" style="padding: 14px; border: 1px solid #1e3a8a; font-weight: 600; text-align: center; font-size: 14px; letter-spacing: 0.5px;">
              SEMESTER 1
            </th>
            <th colspan="2" style="padding: 14px; border: 1px solid #1e3a8a; font-weight: 600; text-align: center; font-size: 14px; letter-spacing: 0.5px;">
              SEMESTER 2
            </th>
          </tr>
          <tr style="background-color: #f1f5f9; color: #1e3a8a;">
            <th style="padding: 10px 12px; border: 1px solid #cbd5e1; font-weight: 600; font-size: 12px; text-align: left;">CLASS</th>
            <th style="padding: 10px 12px; border: 1px solid #cbd5e1; font-weight: 600; text-align: center; width: 100px; font-size: 12px;">MINUTES</th>
            <th style="padding: 10px 12px; border: 1px solid #cbd5e1; font-weight: 600; font-size: 12px; text-align: left;">CLASS</th>
            <th style="padding: 10px 12px; border: 1px solid #cbd5e1; font-weight: 600; text-align: center; width: 100px; font-size: 12px;">MINUTES</th>
          </tr>
        </thead>
        <tbody>
          ${renderClassRows(variables)}
        </tbody>
      </table>
    </div>

    <!-- RUAH Term-Based Classes Section -->
    ${renderRuahSection(variables)}

    <!-- Load Summary: per semester on a Full Year letter (#290) -->
    ${variables.semesterLoads ? renderSemesterLoadSummary(variables) : `
    <div style="margin-top: 30px;">
      <h3 style="font-size: 16px; font-weight: bold; color: #1e3a8a; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 3px solid #3b82f6;">
        LOAD SUMMARY
      </h3>
      <table style="width: 100%; border-collapse: collapse; border: 2px solid #1e3a8a; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
        <tr>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background: linear-gradient(to right, #f1f5f9, #ffffff); font-weight: 600; width: 50%; color: #1e3a8a; font-size: 13px;">
            ALLOWANCES:
          </td>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background-color: #ffffff; font-weight: 600; color: #374151; font-size: 14px;">
            ${variables.allowanceMinutes} mins
          </td>
        </tr>
        <tr>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background: linear-gradient(to right, #f1f5f9, #ffffff); font-weight: 600; color: #1e3a8a; font-size: 13px;">
            ALLOCATED CLASSES:
          </td>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background-color: #ffffff; font-weight: 600; color: #374151; font-size: 14px;">
            ${variables.regularClassMinutes} mins
          </td>
        </tr>
        <tr>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background: linear-gradient(to right, #f1f5f9, #ffffff); font-weight: 600; color: #1e3a8a; font-size: 13px;">
            RUAH (${variables.semesterKey === 'semester2' ? 'Term 3' : variables.semesterKey === 'both' ? 'Terms 1 & 3' : 'Term 1'}):
          </td>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background-color: #ffffff; font-weight: 600; color: #7c3aed; font-size: 14px;">
            ${variables.ruahClassMinutes} mins
          </td>
        </tr>
        <tr>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background: linear-gradient(to right, #f1f5f9, #ffffff); font-weight: 600; color: #1e3a8a; font-size: 13px;">
            TIMETABLED SUBS:
          </td>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; background-color: #ffffff; font-weight: 600; color: #059669; font-size: 14px;">
            ${variables.timetabledSubs} mins
          </td>
        </tr>
        <tr style="background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); color: white;">
          <td style="padding: 14px 16px; border: 1px solid #1e3a8a; font-weight: 700; font-size: 14px;">
            ${variables.semester}:
          </td>
          <td style="padding: 14px 16px; border: 1px solid #1e3a8a; font-weight: 700; font-size: 15px;">
            ${variables.targetMinutes} mins
          </td>
        </tr>
        <tr style="background-color: #f8fafc;">
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; font-weight: 600; color: #1e3a8a; font-size: 13px;">
            FTE:
          </td>
          <td style="padding: 14px 16px; border: 1px solid #cbd5e1; font-weight: 700; font-size: 16px; color: ${getFteColor(variables.fte)};">
            ${variables.fte.toFixed(2)}
          </td>
        </tr>
      </table>
    </div>`}
  `;
};

/**
 * Full Year letter Load Summary: one column per semester, each a fortnight's
 * load against the fortnightly target, so neither is inflated by the other.
 */
const renderSemesterLoadSummary = (variables: TemplateVariables): string => {
  const { semester1: s1, semester2: s2 } = variables.semesterLoads!;
  const label = 'padding: 12px 16px; border: 1px solid #cbd5e1; background: linear-gradient(to right, #f1f5f9, #ffffff); font-weight: 600; color: #1e3a8a; font-size: 13px;';
  const cell = 'padding: 12px 16px; border: 1px solid #cbd5e1; background-color: #ffffff; font-weight: 600; color: #374151; font-size: 14px; text-align: center;';
  const row = (name: string, a: string, b: string, extra = '') => `
        <tr>
          <td style="${label}">${name}</td>
          <td style="${cell}${extra}">${a}</td>
          <td style="${cell}${extra}">${b}</td>
        </tr>`;
  const util = (u: number) => `<span style="color: ${getUtilizationColor(u)}; font-weight: 700;">${u}%</span>`;
  return `
    <div style="margin-top: 30px;">
      <h3 style="font-size: 16px; font-weight: bold; color: #1e3a8a; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 3px solid #3b82f6;">
        LOAD SUMMARY (PER FORTNIGHT)
      </h3>
      <table style="width: 100%; border-collapse: collapse; border: 2px solid #1e3a8a; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
        <tr style="background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); color: white;">
          <th style="padding: 12px 16px; border: 1px solid #1e3a8a; text-align: left; font-size: 13px; width: 40%;"></th>
          <th style="padding: 12px 16px; border: 1px solid #1e3a8a; font-size: 13px;">SEMESTER 1</th>
          <th style="padding: 12px 16px; border: 1px solid #1e3a8a; font-size: 13px;">SEMESTER 2</th>
        </tr>
        ${row('ALLOWANCES:', `${s1.allowanceMinutes} mins`, `${s2.allowanceMinutes} mins`)}
        ${row('ALLOCATED CLASSES:', `${s1.regularClassMinutes} mins`, `${s2.regularClassMinutes} mins`)}
        ${row('RUAH:', `${s1.ruahMinutes} mins <span style="font-size: 11px; color: #6b7280;">(Term 1)</span>`, `${s2.ruahMinutes} mins <span style="font-size: 11px; color: #6b7280;">(Term 3)</span>`, ' color: #7c3aed;')}
        ${row('TIMETABLED SUBS:', `${s1.timetabledSubs} mins`, `${s2.timetabledSubs} mins`, ' color: #059669;')}
        ${row('TOTAL ALLOCATED:', `${s1.allocatedMinutes} mins`, `${s2.allocatedMinutes} mins`)}
        ${row('UTILISATION:', util(s1.utilization), util(s2.utilization))}
        <tr style="background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); color: white;">
          <td style="padding: 12px 16px; border: 1px solid #1e3a8a; font-weight: 700; font-size: 14px;">LOAD (EACH SEMESTER):</td>
          <td colspan="2" style="padding: 12px 16px; border: 1px solid #1e3a8a; font-weight: 700; font-size: 15px; text-align: center;">${variables.targetMinutes} mins</td>
        </tr>
        <tr style="background-color: #f8fafc;">
          <td style="padding: 12px 16px; border: 1px solid #cbd5e1; font-weight: 600; color: #1e3a8a; font-size: 13px;">FTE:</td>
          <td colspan="2" style="padding: 12px 16px; border: 1px solid #cbd5e1; font-weight: 700; font-size: 16px; text-align: center; color: ${getFteColor(variables.fte)};">${variables.fte.toFixed(2)}</td>
        </tr>
      </table>
    </div>
  `;
};

/**
 * Render RUAH term-based classes section - Compact Format
 */
const renderRuahSection = (variables: TemplateVariables): string => {
  // Check if there are any RUAH classes
  const hasRuahClasses =
    (variables.ruahTerm1Classes && variables.ruahTerm1Classes.length > 0) ||
    (variables.ruahTerm2Classes && variables.ruahTerm2Classes.length > 0) ||
    (variables.ruahTerm3Classes && variables.ruahTerm3Classes.length > 0) ||
    (variables.ruahTerm4Classes && variables.ruahTerm4Classes.length > 0);

  if (!hasRuahClasses) {
    return '';
  }

  // Which RUAH terms count toward this letter depends on the semester:
  // Term 1 -> Semester 1, Term 3 -> Semester 2, both for full year.
  // (Defaults to Term 1 counted when semesterKey is absent, preserving prior behaviour.)
  const term1Counted = variables.semesterKey !== 'semester2';
  const term3Counted = variables.semesterKey === 'semester2' || variables.semesterKey === 'both';

  // Helper function to render a single term row
  const renderTermRow = (classes: LoadLetterClass[], termName: string, isCounted: boolean) => {
    const totalMinutes = classes.reduce((sum, cls) => sum + cls.minutesPerFortnight, 0);
    const classList = classes.length > 0
      ? classes.map(cls => `${cls.className} (${cls.minutesPerFortnight})`).join(', ')
      : 'No classes';
    const countedBadge = isCounted
      ? '<span style="background-color: #7c3aed; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600; margin-left: 8px;">COUNTED</span>'
      : '';

    return `
      <tr style="background-color: ${isCounted ? '#faf5ff' : '#ffffff'};">
        <td style="padding: 8px 12px; border: 1px solid #e9d5ff; font-weight: 600; color: ${isCounted ? '#7c3aed' : '#6b7280'}; white-space: nowrap;">
          ${termName}${countedBadge}
        </td>
        <td style="padding: 8px 12px; border: 1px solid #e9d5ff; color: #374151; font-size: 13px;">
          ${classList}
        </td>
        <td style="padding: 8px 12px; border: 1px solid #e9d5ff; text-align: center; font-weight: 600; color: ${isCounted ? '#7c3aed' : '#6b7280'}; white-space: nowrap;">
          ${totalMinutes > 0 ? `${totalMinutes} mins` : '—'}
        </td>
      </tr>
    `;
  };

  return `
    <!-- RUAH Term-Based Classes - Compact Format -->
    <div style="margin-bottom: 30px;">
      <h3 style="font-size: 16px; font-weight: bold; color: #7c3aed; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 3px solid #9333ea;">
        RUAH CLASSES (Term-Based)
      </h3>

      <table style="width: 100%; border-collapse: collapse; border: 2px solid #9333ea; box-shadow: 0 2px 4px rgba(147,51,234,0.1);">
        <thead>
          <tr style="background: linear-gradient(135deg, #7c3aed 0%, #9333ea 100%); color: white;">
            <th style="padding: 10px 12px; border: 1px solid #7c3aed; font-weight: 600; text-align: left; font-size: 12px; width: 100px;">Term</th>
            <th style="padding: 10px 12px; border: 1px solid #7c3aed; font-weight: 600; text-align: left; font-size: 12px;">Classes</th>
            <th style="padding: 10px 12px; border: 1px solid #7c3aed; font-weight: 600; text-align: center; font-size: 12px; width: 100px;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${renderTermRow(variables.ruahTerm1Classes || [], 'Term 1', term1Counted)}
          ${renderTermRow(variables.ruahTerm2Classes || [], 'Term 2', false)}
          ${renderTermRow(variables.ruahTerm3Classes || [], 'Term 3', term3Counted)}
          ${renderTermRow(variables.ruahTerm4Classes || [], 'Term 4', false)}
        </tbody>
      </table>
    </div>
  `;
};

/**
 * Render Leadership Allowances section
 */
const renderAllowancesSection = (variables: TemplateVariables): string => {
  if (!variables.allowances || variables.allowances.length === 0) {
    return '';
  }

  return `
    <!-- Leadership Allowances Section -->
    <div style="margin-bottom: 30px;">
      <h3 style="font-size: 16px; font-weight: bold; color: #1e3a8a; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 3px solid #9333ea;">
        LEADERSHIP ALLOWANCES
      </h3>
      <table style="width: 100%; border-collapse: collapse; border: 2px solid #9333ea; box-shadow: 0 2px 4px rgba(147,51,234,0.1);">
        <thead>
          <tr style="background: linear-gradient(135deg, #7c3aed 0%, #9333ea 100%); color: white;">
            <th style="padding: 14px 16px; border: 1px solid #7c3aed; font-weight: 600; text-align: left; font-size: 13px; letter-spacing: 0.5px;">
              Position / Allowance
            </th>
            <th style="padding: 14px 16px; border: 1px solid #7c3aed; font-weight: 600; text-align: left; font-size: 13px; letter-spacing: 0.5px;">
              Description
            </th>
            <th style="padding: 14px 16px; border: 1px solid #7c3aed; font-weight: 600; text-align: center; width: 120px; font-size: 13px; letter-spacing: 0.5px;">
              Minutes/FN
            </th>
          </tr>
        </thead>
        <tbody>
          ${variables.allowances.map((allowance, idx) => {
            const isEven = idx % 2 === 0;
            return `
              <tr style="background-color: ${isEven ? '#ffffff' : '#faf5ff'};">
                <td style="padding: 12px 16px; border: 1px solid #e9d5ff; font-size: 13px; font-weight: 600; color: #1e3a8a;">
                  ${allowance.type}
                </td>
                <td style="padding: 12px 16px; border: 1px solid #e9d5ff; font-size: 13px; color: #374151;">
                  ${allowance.description || '—'}
                </td>
                <td style="padding: 12px 16px; border: 1px solid #e9d5ff; text-align: center; font-size: 13px; font-weight: 600; color: #7c3aed;">
                  ${allowance.minutesPerFortnight}
                </td>
              </tr>
            `;
          }).join('')}
          <tr style="background: linear-gradient(to right, #faf5ff, #ffffff); border-top: 2px solid #7c3aed;">
            <td colspan="2" style="padding: 14px 16px; border: 1px solid #7c3aed; font-weight: 700; text-align: right; font-size: 14px; color: #1e3a8a;">
              TOTAL ALLOWANCES:
            </td>
            <td style="padding: 14px 16px; border: 1px solid #7c3aed; text-align: center; font-weight: 700; font-size: 15px; color: #7c3aed;">
              ${variables.allowanceMinutes}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `;
};

/**
 * Render class rows - matching classes side by side for both semesters
 */
const renderClassRows = (variables: TemplateVariables): string => {
  // Filter out RUAH/term-based classes - they're shown in separate section
  const sem1Classes = variables.semester1Classes.filter(c => !c.isRuah && !c.term);
  const sem2Classes = variables.semester2Classes.filter(c => !c.isRuah && !c.term);
  const maxRows = Math.max(sem1Classes.length, sem2Classes.length); // Display only actual class rows

  let rows = '';

  for (let i = 0; i < maxRows; i++) {
    const class1 = sem1Classes[i];
    const class2 = sem2Classes[i];
    const isEvenRow = i % 2 === 0;

    // Helper function to format class name with code for ORATORY subjects
    const formatClassName = (classItem: typeof class1) => {
      if (!classItem) return '&nbsp;';
      const isOratory = classItem.className.toUpperCase().startsWith('ORATORY');
      const displayName = isOratory && classItem.subjectCode
        ? `${classItem.className} - ${classItem.subjectCode}`
        : classItem.className;
      return `<strong>${displayName}</strong>`;
    };

    rows += `
      <tr style="background-color: ${isEvenRow ? '#ffffff' : '#f8fafc'};">
        <td style="padding: 10px 12px; border: 1px solid #e2e8f0; font-size: 13px; color: #374151;">
          ${formatClassName(class1)}
        </td>
        <td style="padding: 10px 12px; border: 1px solid #e2e8f0; text-align: center; font-size: 13px; font-weight: 600; color: #1e3a8a;">
          ${class1 ? class1.minutesPerFortnight : '&nbsp;'}
        </td>
        <td style="padding: 10px 12px; border: 1px solid #e2e8f0; font-size: 13px; color: #374151;">
          ${formatClassName(class2)}
        </td>
        <td style="padding: 10px 12px; border: 1px solid #e2e8f0; text-align: center; font-size: 13px; font-weight: 600; color: #1e3a8a;">
          ${class2 ? class2.minutesPerFortnight : '&nbsp;'}
        </td>
      </tr>
    `;
  }

  return rows;
};

/**
 * Render allowances list for Other Allowances section
 */
const renderAllowancesList = (allowances: LoadLetterAllowance[]): string => {
  if (!allowances || allowances.length === 0) {
    return '<div style="font-size: 12px; color: #9ca3af;">No additional allowances</div>';
  }

  const allowanceItems = allowances.map(allowance => {
    const description = allowance.description ? ` (${allowance.description})` : '';
    return `
      <div style="font-size: 12px; color: #6b7280; padding: 3px 0; border-bottom: 1px solid #f1f5f9;">
        <span style="font-weight: 500; color: #374151;">${allowance.type}</span>${description}:
        <span style="font-weight: 600; color: #1e3a8a;">${allowance.minutesPerFortnight} mins</span>
      </div>
    `;
  }).join('');

  return `<div style="margin-top: 8px;">${allowanceItems}</div>`;
};

/**
 * Get color for FTE value
 */
const getFteColor = (fte: number): string => {
  if (fte >= 1.0) return '#059669'; // Full time - green
  if (fte >= 0.8) return '#2563eb'; // High part-time - blue
  if (fte >= 0.5) return '#7c3aed'; // Mid part-time - purple
  return '#dc2626'; // Low part-time - red
};

/**
 * Legacy template renderer (for backward compatibility)
 */
const renderLegacyTemplate = (template: LoadLetterTemplate, variables: TemplateVariables): string => {
  const fullTemplate = `
    <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; line-height: 1.6; color: #333;">
      ${template.headerHtml}
      ${template.introductionHtml}

      <!-- Load Summary Table -->
      ${renderLoadSummaryTable(variables)}

      <!-- Classes Tables (Semester 1 and 2 side by side) -->
      ${renderClassesTables(variables)}

      <!-- Allowances/Positions Table -->
      ${renderAllowancesTable(variables)}

      ${template.modelExplanationHtml}
      ${template.yardDutyHtml}
      ${template.footerHtml}
    </div>
  `;

  return replaceVariables(fullTemplate, variables);
};

/**
 * Replace simple variables like {{staffName}}, {{fte}}, etc.
 */
const replaceVariables = (html: string, variables: TemplateVariables): string => {
  let result = html;

  // Staff info
  result = result.replace(/\{\{staffName\}\}/g, variables.staffName);
  result = result.replace(/\{\{staffCode\}\}/g, variables.staffCode);
  result = result.replace(/\{\{department\}\}/g, variables.department);
  result = result.replace(/\{\{curriculumArea\}\}/g, variables.curriculumArea || '');

  // Academic info
  result = result.replace(/\{\{academicYear\}\}/g, variables.academicYear);
  result = result.replace(/\{\{semester\}\}/g, variables.semester);
  result = result.replace(/\{\{issueDate\}\}/g, formatDate(variables.issueDate));
  result = result.replace(/\{\{dueDate\}\}/g, formatDate(variables.dueDate));
  result = result.replace(/\{\{priorYear\}\}/g, variables.priorYear || (parseInt(variables.academicYear) - 1).toString());

  // Numeric values
  result = result.replace(/\{\{fte\}\}/g, variables.fte.toFixed(2));
  result = result.replace(/\{\{targetMinutes\}\}/g, variables.targetMinutes.toString());
  result = result.replace(/\{\{allocatedMinutes\}\}/g, variables.allocatedMinutes.toString());
  result = result.replace(/\{\{teachingMinutes\}\}/g, variables.teachingMinutes.toString());
  result = result.replace(/\{\{allowanceMinutes\}\}/g, variables.allowanceMinutes.toString());
  result = result.replace(/\{\{timetabledSubs\}\}/g, variables.timetabledSubs.toString());
  result = result.replace(/\{\{utilization\}\}/g, variables.utilization.toString());

  // College info
  result = result.replace(/\{\{collegeName\}\}/g, variables.collegeName || 'College Name');
  result = result.replace(/\{\{collegeAddress\}\}/g, variables.collegeAddress || '');
  result = result.replace(/\{\{collegePhone\}\}/g, variables.collegePhone || '');
  result = result.replace(/\{\{collegeEmail\}\}/g, variables.collegeEmail || '');
  result = result.replace(/\{\{collegeWebsite\}\}/g, variables.collegeWebsite || '');
  result = result.replace(/\{\{collegeTagline\}\}/g, variables.collegeTagline || '');

  // Principal info
  result = result.replace(/\{\{principalName\}\}/g, variables.principalName || 'College Principal');

  // Letter-specific
  result = result.replace(/\{\{meaGuidelinesUrl\}\}/g, variables.meaGuidelinesUrl || '#');
  result = result.replace(/\{\{polPosition\}\}/g, variables.polPosition || '');
  result = result.replace(/\{\{polMinutes\}\}/g, (variables.polMinutes || 0).toString());
  result = result.replace(/\{\{otherAllowance\}\}/g, (variables.otherAllowance || variables.allowanceMinutes).toString());

  return result;
};

/**
 * Legacy: Render the load summary table
 */
const renderLoadSummaryTable = (variables: TemplateVariables): string => {
  return `
    <div style="margin-bottom: 30px;">
      <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">
        Teaching Load Summary
      </h2>
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <tbody>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5; font-weight: bold; width: 40%;">FTE</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${variables.fte.toFixed(2)}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5; font-weight: bold;">Target Minutes (per fortnight)</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${variables.targetMinutes}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5; font-weight: bold;">Teaching Minutes</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${variables.teachingMinutes}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5; font-weight: bold;">Other Allowances</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${variables.allowanceMinutes}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5; font-weight: bold;">Total Load</td>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">${variables.allocatedMinutes}</td>
          </tr>
          ${variables.timetabledSubs > 0 ? `
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5; font-weight: bold;">Timetabled Subs</td>
            <td style="padding: 8px; border: 1px solid #ddd; color: #0066cc;">${variables.timetabledSubs}</td>
          </tr>
          ` : ''}
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; background-color: #f5f5f5; font-weight: bold;">Utilization</td>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; color: ${getUtilizationColor(variables.utilization)};">
              ${variables.utilization}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `;
};

/**
 * Legacy: Render classes tables for both semesters side by side
 */
const renderClassesTables = (variables: TemplateVariables): string => {
  const hasSemester1 = variables.semester1Classes.length > 0;
  const hasSemester2 = variables.semester2Classes.length > 0;

  if (!hasSemester1 && !hasSemester2) {
    return '<div style="margin-bottom: 30px;"><p style="color: #666; font-style: italic;">No classes assigned</p></div>';
  }

  return `
    <div style="margin-bottom: 30px;">
      <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">
        Class Assignments
      </h2>
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="padding: 10px; border: 1px solid #ddd; background-color: #e8e8e8; font-weight: bold; text-align: center; width: 50%;">
              SEMESTER 1
            </th>
            <th style="padding: 10px; border: 1px solid #ddd; background-color: #e8e8e8; font-weight: bold; text-align: center; width: 50%;">
              SEMESTER 2
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 10px; border: 1px solid #ddd; vertical-align: top;">
              ${renderSemesterClasses(variables.semester1Classes)}
            </td>
            <td style="padding: 10px; border: 1px solid #ddd; vertical-align: top;">
              ${renderSemesterClasses(variables.semester2Classes)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `;
};

/**
 * Legacy: Render classes for a single semester
 */
const renderSemesterClasses = (classes: LoadLetterClass[]): string => {
  if (classes.length === 0) {
    return '<p style="color: #999; font-style: italic; margin: 0;">No classes</p>';
  }

  const classRows = classes.map(cls => `
    <tr>
      <td style="padding: 6px 8px; border-bottom: 1px solid #eee;">
        <strong>${cls.className}</strong><br/>
        <span style="font-size: 12px; color: #666;">${cls.classCode}</span>
      </td>
      <td style="padding: 6px 8px; border-bottom: 1px solid #eee; text-align: center;">
        ${cls.periodsPerWeek}
      </td>
      <td style="padding: 6px 8px; border-bottom: 1px solid #eee; text-align: right;">
        ${cls.minutesPerFortnight}
      </td>
    </tr>
  `).join('');

  return `
    <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
      <thead>
        <tr style="background-color: #f9f9f9;">
          <th style="padding: 6px 8px; text-align: left; font-size: 12px; font-weight: bold; border-bottom: 2px solid #ddd;">Class</th>
          <th style="padding: 6px 8px; text-align: center; font-size: 12px; font-weight: bold; border-bottom: 2px solid #ddd;">Periods</th>
          <th style="padding: 6px 8px; text-align: right; font-size: 12px; font-weight: bold; border-bottom: 2px solid #ddd;">Minutes</th>
        </tr>
      </thead>
      <tbody>
        ${classRows}
      </tbody>
    </table>
  `;
};

/**
 * Legacy: Render allowances table
 */
const renderAllowancesTable = (variables: TemplateVariables): string => {
  if (variables.allowances.length === 0) {
    return '';
  }

  const allowanceRows = variables.allowances.map(allowance => {
    const percentageRelease = allowance.percentageRelease
      ? ` (${allowance.percentageRelease}% release)`
      : '';

    return `
      <tr>
        <td style="padding: 8px; border: 1px solid #ddd;">
          <strong>${allowance.type}</strong>${percentageRelease}
          ${allowance.description ? `<br/><span style="font-size: 12px; color: #666;">${allowance.description}</span>` : ''}
        </td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: right; font-weight: bold;">
          ${allowance.minutesPerFortnight}
        </td>
      </tr>
    `;
  }).join('');

  return `
    <div style="margin-bottom: 30px;">
      <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">
        Positions & Allowances
      </h2>
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #e8e8e8;">
            <th style="padding: 10px; border: 1px solid #ddd; text-align: left; font-weight: bold;">Position/Allowance</th>
            <th style="padding: 10px; border: 1px solid #ddd; text-align: right; font-weight: bold; width: 150px;">Minutes (per fn)</th>
          </tr>
        </thead>
        <tbody>
          ${allowanceRows}
        </tbody>
      </table>
    </div>
  `;
};

/**
 * Format a date string for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  return date.toLocaleDateString('en-AU', options);
};

/**
 * Get color for utilization percentage
 */
const getUtilizationColor = (utilization: number): string => {
  if (utilization < 85) return '#3b82f6'; // Under-utilized (blue)
  if (utilization < 95) return '#06b6d4'; // Slightly under (cyan)
  if (utilization <= 105) return '#059669'; // Balanced (green)
  if (utilization <= 115) return '#f59e0b'; // Slightly over (amber)
  return '#dc2626'; // Over-utilized (red)
};

/**
 * Generate preview HTML with sample data
 */
export const generatePreviewHtml = (template: LoadLetterTemplate): string => {
  const sampleVariables: TemplateVariables = {
    staffName: 'Marielle Caruana',
    staffCode: 'MAR',
    department: 'Teaching',
    curriculumArea: 'The Arts',
    academicYear: '2025',
    semester: 'Semester 1',
    issueDate: '2024-11-11',
    dueDate: '2024-11-25',
    fte: 0.60,
    targetMinutes: 1332,
    allocatedMinutes: 1332,
    teachingMinutes: 982,
    allowanceMinutes: 350,
    timetabledSubs: 0,
    utilization: 100,
    regularClassMinutes: 880,
    ruahClassMinutes: 102,
    collegeName: 'Salesian College',
    collegeAddress: 'Sunbury 3429, Vic, Australia',
    collegePhone: 'F: 03 9744 6182',
    collegeEmail: 'info@scrvic.edu.au',
    collegeWebsite: 'scrvic.edu.au',
    collegeTagline: 'Engaging with Community | Passion for Learning | Striving for Excellence | Inspired by Faith',
    principalName: 'Mark Brockhus',
    priorYear: '2024',
    meaGuidelinesUrl: 'https://example.com/mea-guidelines',
    polPosition: 'Community Engagement Co-ordinator',
    polMinutes: 270,
    otherAllowance: 350,
    semester1Classes: [
      {
        id: '1',
        classCode: '09ART01',
        className: 'Discover Arts MAD',
        subjectCode: 'ART',
        subjectName: 'Visual Arts',
        yearLevel: 9,
        semester: 'semester1',
        periodsPerWeek: 6,
        minutesPerFortnight: 264,
      },
      {
        id: '2',
        classCode: '10ART01',
        className: 'Discover Arts MAD',
        subjectCode: 'ART',
        subjectName: 'Visual Arts',
        yearLevel: 10,
        semester: 'semester1',
        periodsPerWeek: 4,
        minutesPerFortnight: 352,
      },
    ],
    semester2Classes: [
      {
        id: '3',
        classCode: '10ART02',
        className: 'Explore Arts Art Now',
        subjectCode: 'ART',
        subjectName: 'Visual Arts',
        yearLevel: 10,
        semester: 'semester2',
        periodsPerWeek: 6,
        minutesPerFortnight: 264,
      },
    ],
    allowances: [
      {
        id: '1',
        type: 'Oratory Leader',
        description: 'MP222',
        minutesPerFortnight: 80,
        semester: 'both',
      },
      {
        id: '2',
        type: 'Community Engagement Co-ordinator',
        description: '',
        minutesPerFortnight: 270,
        percentageRelease: 12,
        semester: 'both',
      },
    ],
  };

  return renderTemplate(template, sampleVariables);
};
